import React from 'react';
import { Outlet, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../../context/useAuth';
import Breadcrumbs, { BreadcrumbItem } from '../../common/Breadcrumbs';
import Logout from '../../auth/Logout';

// Admin navigation items
const adminNavItems = [
  { name: 'Dashboard', href: '/admin' },
  { name: 'Team Management', href: '/admin/team' },
  { name: 'Schedules', href: '/admin/schedules' },
  { name: 'Settings', href: '/admin/settings' },
];

// Helper function to generate breadcrumb items from current path
const generateAdminBreadcrumbs = (path: string): BreadcrumbItem[] => {
  // Always include home and admin
  const breadcrumbs: BreadcrumbItem[] = [
    { label: 'Home', href: '/dashboard' },
    { label: 'Admin', href: '/admin' }
  ];
  
  // Skip if we're already on the admin dashboard
  if (path === '/admin') {
    return breadcrumbs;
  }
  
  // Split the path into segments and build breadcrumbs
  const segments = path.split('/').filter(Boolean);
  
  // Skip the 'admin' part since we already added it
  const relevantSegments = segments.slice(1);
  
  relevantSegments.forEach((segment, index) => {
    // Convert segment to a readable label (capitalize, replace dashes with spaces)
    const label = segment
      .replace(/-/g, ' ')
      .replace(/^\w/, c => c.toUpperCase());
    
    // Current path up to this segment
    const href = `/admin/${relevantSegments.slice(0, index + 1).join('/')}`;
    
    breadcrumbs.push({ label, href });
  });
  
  return breadcrumbs;
};

const AdminLayout: React.FC = () => {
  const { user } = useAuth();
  const location = useLocation();
  
  const breadcrumbs = generateAdminBreadcrumbs(location.pathname);

  return (
    <div className="flex h-screen bg-gray-50">
      {/* Sidebar */}
      <aside className="bg-gray-800 w-64">
        {/* Logo */}
        <div className="flex h-16 items-center justify-center border-b border-gray-700">
          <h1 className="text-2xl font-bold text-white">Admin Panel</h1>
        </div>
        
        {/* Navigation */}
        <nav className="mt-6 px-3">
          <ul className="space-y-2">
            {adminNavItems.map((item) => {
              const isActive = location.pathname === item.href || 
                (item.href !== '/admin' && location.pathname.startsWith(item.href));
              
              return (
                <li key={item.name}>
                  <Link
                    to={item.href}
                    className={`flex items-center py-2 px-3 rounded-md ${
                      isActive 
                        ? 'bg-gray-900 text-white'
                        : 'text-gray-300 hover:bg-gray-700 hover:text-white'
                    }`}
                  >
                    <span>{item.name}</span>
                  </Link>
                </li>
              );
            })}
          </ul>
        </nav>
        
        {/* Back to app */}
        <div className="absolute bottom-4 left-0 right-0 px-4">
          <Link
            to="/dashboard"
            className="flex items-center w-full py-2 px-3 rounded-md text-gray-300 hover:bg-gray-700 hover:text-white"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clipRule="evenodd" />
            </svg>
            Back to App
          </Link>
        </div>
      </aside>
      
      {/* Main content */}
      <div className="flex flex-1 flex-col overflow-hidden">
        {/* Header */}
        <header className="bg-white shadow-sm z-10">
          <div className="px-4 py-4 sm:px-6 lg:px-8 flex items-center justify-between">
            {/* Breadcrumbs */}
            <Breadcrumbs items={breadcrumbs} />
            
            {/* User menu */}
            <div className="flex items-center space-x-4">
              <div className="text-sm flex items-center">
                <span className="text-gray-700">Admin: {user?.email}</span>
                <div className="mx-2 h-4 w-px bg-gray-200"></div>
                <Logout className="text-sm" />
              </div>
            </div>
          </div>
        </header>
        
        {/* Page content */}
        <main className="flex-1 overflow-y-auto p-4 sm:p-6 lg:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;
